import { sum } from "lodash";
import { createSelector } from "reselect";

import { SaleLotType } from "constants/saleLots";

import { getLivestockSaleId } from "lib/navigation";
import { getBestMatchingSplit } from "lib/splits";

import { selectDisplayedAgentByVendorId } from "../agencies";
import { selectOutOfSyncFieldNamesByBusinessId } from "../businesses";
import {
  selectResolvedSaleLotCommentCountBySaleLotIdLookup,
  selectUnresolvedSaleLotCommentCountBySaleLotIdLookup,
} from "../comments";
import { selectAgencyBySaleLotIdLookup } from "../consignmentsCalculated";
import { getCurrentSale } from "../currentSale";
import { selectPenScanLotIdsBySaleLotIdLookup } from "../globalSearch/penScanLotFilters";
import { selectFilteredSaleLotIds } from "../globalSearch/saleLotFilters";
import {
  selectEidsByPenScanLotIdLookup,
  selectEidsBySaleLotIdLookup,
} from "../indexes";
import {
  getAuctionPens,
  getBusinesses,
  getLabels,
  getPenScanLots,
  getSaleSubTypes,
  getSpecies,
} from "../root";
import {
  getSaleLotsBySale,
  selectNeverEverAccreditationStatusBySaleLotIdLookup,
  selectXeroBusinessesBySaleLotIdLookup,
} from "../saleLots/index";
import { selectEffectiveSaleTitleBySaleIdLookup } from "../sales";
import { selectDeploymentIdBySaleLotIdLookup } from "../saleLotsCalculated";
import { selectNotAngusVerifiedCountBySaleLotIdLookup } from "../scans";
import { selectDeploymentMarkOrderLookup } from "../speciesAttributes";
import {
  selectBuyerSplitByKeyLookup,
  selectVendorSplitByKeyLookup,
} from "../splits";

const selectEnhancedCurrentSale = createSelector(
  [getCurrentSale, getSaleSubTypes, getSpecies],
  (sale, saleSubTypes, species) =>
    Object.assign({}, sale, {
      species: species[sale.species_id],
      saleSubType: saleSubTypes[sale.sale_sub_type_id],
    }),
);

export const selectGeneralSaleLotsAggridData = createSelector(
  [
    getSaleLotsBySale,
    selectUnresolvedSaleLotCommentCountBySaleLotIdLookup,
    selectResolvedSaleLotCommentCountBySaleLotIdLookup,
    selectAgencyBySaleLotIdLookup,
    selectDisplayedAgentByVendorId,
    selectNotAngusVerifiedCountBySaleLotIdLookup,
    getLabels,
    selectFilteredSaleLotIds,
    getBusinesses,
    selectOutOfSyncFieldNamesByBusinessId,
    selectXeroBusinessesBySaleLotIdLookup,
    selectPenScanLotIdsBySaleLotIdLookup,
    getPenScanLots,
    getAuctionPens,
    selectEidsByPenScanLotIdLookup,
    selectEidsBySaleLotIdLookup,
    selectEnhancedCurrentSale,
    selectDeploymentMarkOrderLookup,
    selectNeverEverAccreditationStatusBySaleLotIdLookup,
    selectDeploymentIdBySaleLotIdLookup,
    selectVendorSplitByKeyLookup,
    selectBuyerSplitByKeyLookup,
    selectEffectiveSaleTitleBySaleIdLookup,
  ],
  (
    saleLots,
    unresolvedSaleLotCommentCountBySaleLotIdLookup,
    resolvedSaleLotCommentCountBySaleLotIdLookup,
    agencyBySaleLotIdLookup,
    relationshipAgent,
    notAngusVerifiedCountBySaleLotIdLookup,
    labelsLookup,
    filteredSaleLotIds,
    businesses,
    outOfSyncByBusinessIdLookup,
    xeroBusinessesBySaleLotIdLookup,
    penScanLotIdsBySaleLotId,
    penScanLotByIdLookup,
    auctionPens,
    eidsByPenScanLotIdLookup,
    eidsBySaleLotIdLookup,
    currentSale,
    deploymentMarkOrderLookup,
    neverEverAccreditationStatusBySaleLotIdLookup,
    deploymentIdBySaleLotIdLookup,
    vendorSplitByKeyLookup,
    buyerSplitByKeyLookup,
    effectiveSaleTitleBySaleIdLookup,
  ) =>
    saleLots
      .filter(
        saleLot =>
          filteredSaleLotIds.includes(saleLot.id) &&
          saleLot.saleLotType !== SaleLotType.VENDOR_SPLIT,
      )
      .map(saleLot => {
        const { buyer = {}, vendor = {}, id: saleLotId } = saleLot;
        const xeroBusinesses = xeroBusinessesBySaleLotIdLookup[saleLot.id];
        const vendorId = vendor?.id;
        const relationshipAgentText = relationshipAgent[vendorId];
        const penScanLots = penScanLotIdsBySaleLotId[saleLot.id]?.map(
          penScanLotId => {
            const penScanLot = penScanLotByIdLookup[penScanLotId];
            const sellingPen = auctionPens[penScanLot?.sellingPenId];
            const scansCount =
              eidsByPenScanLotIdLookup[penScanLotId]?.length || 0;
            return Object.assign(
              {
                title: `${sellingPen?.start_pen} ${scansCount} / ${penScanLot?.quantity}`,
              },
              penScanLotByIdLookup?.[penScanLotId],
            );
          },
        );

        const saleLotPenScanLotIds = penScanLotIdsBySaleLotId[saleLotId] || [];

        const saleLotHasPenScanLots = !!saleLotPenScanLotIds?.length;

        const saleLotHasMoreThanOnePenScanLot = saleLotPenScanLotIds.length > 1;

        const saleLotEids = eidsBySaleLotIdLookup[saleLotId] || [];

        const saleLotEidsCount = saleLotEids.length;

        const saleLotPenScanLotEidsCount = sum(
          saleLotPenScanLotIds.map(
            penScanLotId => eidsByPenScanLotIdLookup[penScanLotId].length,
          ),
        );

        const saleLotQuantityDoesNotEqualSaleLotEidsCount =
          saleLot.quantity !== saleLotEidsCount;

        let rowWarning = null;

        // if the sale lot is associated to more than one pen scan lot
        // or the amount of sale lot eids is not equal to the the count
        // of pen scan lot eids

        if (saleLotHasPenScanLots) {
          if (saleLotHasMoreThanOnePenScanLot) {
            rowWarning = "Sale Lot has more than one Pen Scan Lot";
          } else if (saleLotEidsCount !== saleLotPenScanLotEidsCount) {
            rowWarning =
              "Sale Lot Scans count does not equal Pen Scan Lot Scans count";
          } else if (saleLotQuantityDoesNotEqualSaleLotEidsCount) {
            rowWarning =
              "Sale Lot Scans count does not equal Sale Lot hd count";
          }
        }

        const deploymentId = deploymentIdBySaleLotIdLookup[saleLotId];

        const marksWithOrder = saleLot.marks.map(mark => ({
          ...mark,
          order: deploymentMarkOrderLookup[deploymentId]?.[mark.location],
        }));

        const neverEverAccreditationStatus =
          neverEverAccreditationStatusBySaleLotIdLookup[saleLot.id];

        // vendor/buyer splits
        const livestockSaleId = getLivestockSaleId();
        const vendorSplit = getBestMatchingSplit({
          splitByKeyLookup: vendorSplitByKeyLookup,
          primaryBusinessId: vendorId,
          deploymentId,
          livestockSaleId,
          consignmentId: saleLot.consignment_id,
          saleLotId,
        });

        const buyerSplit = getBestMatchingSplit({
          splitByKeyLookup: buyerSplitByKeyLookup,
          primaryBusinessId: buyer.id,
          deploymentId,
          livestockSaleId,
          saleLotId,
        });

        const saleId = currentSale?.livestocksale_id;
        const effectiveTitle = effectiveSaleTitleBySaleIdLookup[saleId];
        return Object.assign({}, saleLot, {
          agency: agencyBySaleLotIdLookup[saleLot.id],
          invoiceToBusiness: businesses[saleLot.invoiceToBusinessId] || {},
          notAngusVerifiedCount:
            notAngusVerifiedCountBySaleLotIdLookup[saleLot.id],
          labels: saleLot.labels?.map(id => labelsLookup[id]?.name || "") || [],
          livestockSale: { ...currentSale, sale_title: effectiveTitle },
          marks: marksWithOrder,
          neverEverAccreditationStatus,
          relationshipAgent: relationshipAgentText,
          resolvedSaleLotCommentCount:
            resolvedSaleLotCommentCountBySaleLotIdLookup[saleLot.id],
          unresolvedSaleLotCommentCount:
            unresolvedSaleLotCommentCountBySaleLotIdLookup[saleLot.id],
          vendor,
          xeroBuyer: xeroBusinesses.buyer,
          xeroVendor: xeroBusinesses.vendor,
          vendorDataOutOfSync: outOfSyncByBusinessIdLookup[vendor?.id],
          buyerDataOutOfSync: outOfSyncByBusinessIdLookup[buyer?.id],
          penScanLots,
          rowWarning,
          transporter: businesses[saleLot.transporterId] || {},
          vendorSplit,
          buyerSplit,
        });
      }),
);
